import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Phone,
  Plus,
  Trash2,
  Edit,
  AlertCircle,
  Calendar,
  Building
} from "lucide-react";

export default function AOCManager({ facilities = [], onUpdate }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [todayAOC, setTodayAOC] = useState(null);
  
  const [formData, setFormData] = useState({
    facility_id: "",
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    shift_type: "full",
    notes: "",
    phone_number: ""
  });

  const fetchShifts = async () => {
    try {
      const [shiftsRes, todayRes] = await Promise.all([
        axios.get(`${API}/aoc`, { withCredentials: true }),
        axios.get(`${API}/aoc/today`, { withCredentials: true })
      ]);
      setShifts(shiftsRes.data || []);
      setTodayAOC(todayRes.data);
    } catch (error) {
      console.error("Error fetching AOC shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  const handleSubmit = async () => {
    if (!formData.facility_id || !formData.start_date) {
      toast.error("Please select a facility and start date");
      return;
    }

    try {
      if (editingShift) {
        await axios.patch(`${API}/aoc/${editingShift.aoc_id}`, formData, { withCredentials: true });
        toast.success("AOC shift updated");
      } else {
        await axios.post(`${API}/aoc`, formData, { withCredentials: true });
        toast.success("AOC shift added");
      }
      
      setShowAddDialog(false);
      setEditingShift(null);
      resetForm();
      fetchShifts();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to save AOC shift");
    }
  };

  const handleDelete = async (aocId) => {
    if (!confirm("Delete this AOC shift?")) return;
    try {
      await axios.delete(`${API}/aoc/${aocId}`, { withCredentials: true });
      toast.success("AOC shift deleted");
      fetchShifts();
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      facility_id: "",
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      shift_type: "full",
      notes: "",
      phone_number: ""
    });
  };

  const openEditDialog = (shift) => {
    setEditingShift(shift);
    setFormData({
      facility_id: shift.facility_id,
      start_date: shift.start_date,
      end_date: shift.end_date,
      shift_type: shift.shift_type || "full",
      notes: shift.notes || "",
      phone_number: shift.phone_number || ""
    });
    setShowAddDialog(true);
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');
    
    if (start === end) {
      return startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const getShiftTypeLabel = (type) => {
    const types = {
      full: "Full Day",
      evening: "Evening/Night",
      weekend: "Weekend"
    };
    return types[type] || type;
  };

  // Sort shifts by start date
  const sortedShifts = [...shifts].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const upcomingShifts = sortedShifts.filter(s => s.end_date >= new Date().toISOString().split('T')[0]);

  return (
    <Card className="bento-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Phone className="w-5 h-5 text-amber-500" />
            Administrator On Call (AOC)
          </CardTitle>
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingShift(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-aoc-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add AOC
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingShift ? "Edit AOC Shift" : "Add AOC Shift"}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Facility *</Label>
                  <Select
                    value={formData.facility_id}
                    onValueChange={(v) => setFormData({ ...formData, facility_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility..." />
                    </SelectTrigger>
                    <SelectContent>
                      {facilities.map(fac => (
                        <SelectItem key={fac.facility_id} value={fac.facility_id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: fac.color || '#8B5CF6' }}
                            />
                            {fac.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      min={formData.start_date}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Shift Type</Label>
                  <Select
                    value={formData.shift_type}
                    onValueChange={(v) => setFormData({ ...formData, shift_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Day (24hr)</SelectItem>
                      <SelectItem value="evening">Evening/Night</SelectItem>
                      <SelectItem value="weekend">Weekend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Number
                  </Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="Phone number while on call"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any special instructions or coverage notes..."
                    className="h-20"
                  />
                </div>
                
                <Button onClick={handleSubmit} className="w-full">
                  {editingShift ? "Update Shift" : "Add AOC Shift"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Today's AOC Status */}
        {todayAOC?.is_on_call && (
          <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/50">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <span className="font-semibold text-amber-300">You're On Call Today!</span>
            </div>
            {todayAOC.shifts.map(shift => (
              <div key={shift.aoc_id} className="mt-2 text-sm">
                <span className="text-amber-200">{shift.facility_name}</span>
                {shift.phone_number && (
                  <span className="ml-2 text-amber-400">📞 {shift.phone_number}</span>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Upcoming Shifts */}
        {upcomingShifts.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground font-medium">Upcoming AOC Shifts</p>
            {upcomingShifts.slice(0, 5).map(shift => (
              <div 
                key={shift.aoc_id}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: shift.facility_color || '#8B5CF6' }}
                  />
                  <div>
                    <p className="font-medium text-sm">{shift.facility_short_name || shift.facility_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {formatDateRange(shift.start_date, shift.end_date)}
                      <Badge variant="outline" className="text-[10px]">
                        {getShiftTypeLabel(shift.shift_type)}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(shift)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(shift.aoc_id)}
                    className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No upcoming AOC shifts</p>
            <p className="text-xs">Add your on-call dates to stay organized</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
